import { NavigateNext, NavigateBefore } from "@mui/icons-material";
import { IconButton } from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";
import CheckBox from "./CheckBox/CheckBox";
import Line from "./Line/Line";
import Rectangle from "./Rectangle/Rectangle";
import RadioButton from "./RadioButton/RadioButton";
import TextBlock from "./TextBlock/TextBlock";
import TextBox from "./TextBox/TextBox";
import "./XFDLViewer.css";
import PropTypes from "prop-types";

function XFDLViewer(props) {
  const {
    document,
    fileName,
    formData,
    requiredFields,
    isSubmitted,
    showPageNumbers,
    previewType,
  } = props;
  const [pageIndex, setPageIndex] = useState(0);

  // PreviewType 1: First page only, PreviewType 2: All pages
  const isFirstPageOnly = previewType === 1;
  const [canvas, setCanvas] = useState(document?.Canvas?.[0] ?? null);
  const prefilledKeys = useMemo(() => {
    return new Set(
      formData
        ?.filter(
          (el) =>
            el.value !== "" && el.value !== null && el.value !== undefined,
        )
        .map((el) => el.key),
    );
  }, [fileName]);

  useEffect(() => {
    setCanvas(document?.Canvas?.[pageIndex] ?? null);
  }, [pageIndex, formData]);

  useEffect(() => {
    setPageIndex(0);
  }, [fileName]);
  /*istanbul ignore next */
  const onNextHandler = () => {
    // Prevent navigation if preview type is 1 (first page only)
    if (!isFirstPageOnly) {
      setPageIndex(pageIndex + 1);
    }
  };
  /*istanbul ignore next */
  const onPreviousHandler = () => {
    // Prevent navigation if preview type is 1 (first page only)
    if (!isFirstPageOnly) {
      setPageIndex(pageIndex - 1);
    }
  };

  const checkItemInArray = (name) => {
    return formData?.find((el) => el.key === name)?.value || "";
  };

  const checkIsRequired = (name) => {
    let value = requiredFields?.find((el) => el.key === name)?.value || false;
    return Boolean(value);
  };

  const alwaysDisabledKeys = new Set([
    "Producer_FullName_A",
    "Producer_MailingAddress_LineOne_A",
    "Producer_MailingAddress_LineTwo_A",
    "Producer_MailingAddress_CityName_A",
    "Producer_MailingAddress_StateOrProvinceCode_A",
    "Producer_MailingAddress_PostalCode_A",
    "Producer_ContactPerson_PhoneNumber_A",
    "Producer_ContactPerson_FullName_A",
    "Producer_FaxNumber_A",
    "Insurer_FullName_A",
    "Insurer_NAICCode_A",
    "Producer_AuthorizedRepresentative_FullName_A",
    "Producer_StateLicenseIdentifier_A",
    "Producer_NationalIdentifier_A",
  ]);

  const isFieldDisabled = (fieldName) =>
    isSubmitted ||
    props.from !== "share" ||
    (alwaysDisabledKeys.has(fieldName) && prefilledKeys.has(fieldName));

  const whenNotImgData =
    !canvas?.BackgroundRefImage &&
    canvas?.FormType !== "NonAcord" &&
    canvas?.IsStaticLogo ? (
      <div style={{ position: "absolute", top: 10, left: 10 }}>
        <img
          src="/images/AfaveLogo.png"
          alt="logo"
          height="35px"
          width="120px"
        />
      </div>
    ) : null;
  return (
    <>
      <div
        className="viewer-header"
        style={{
          width:
            canvas?.FormType === "NonAcord"
              ? `${canvas.Width}px`
              : `${document.FormViewSize}px`,
        }}
      >
        <div
          id="file-name"
          data-testid="file-name"
          className="viewer-file-name"
        >
          {fileName}
        </div>
        <div className="viewer-navigation">
          {!isFirstPageOnly && (
            <IconButton
              variant="outlined"
              className="viewer-navigation-button"
              onClick={onPreviousHandler}
              disabled={pageIndex === 0}
            >
              <NavigateBefore />
            </IconButton>
          )}
          {showPageNumbers && (
            <span className="viewer-page-number">
              ({pageIndex + 1}/{document.TotalPages})
            </span>
          )}
          {!isFirstPageOnly && (
            <IconButton
              variant="outlined"
              className="viewer-navigation-button"
              onClick={onNextHandler}
              disabled={pageIndex === document?.TotalPages - 1}
            >
              <NavigateNext />
            </IconButton>
          )}
        </div>
      </div>

      <div>
        {canvas && (
          <>
            <div
              className="viewer-container"
              style={{
                height: `${canvas.Height}px`,
                width:
                  canvas?.FormType === "NonAcord"
                    ? `${canvas.Width}px`
                    : `${document.FormViewSize}px`,
                background: canvas?.BackgroundRefImage
                  ? `url("data:image/jpeg;base64,${canvas.BackgroundRefImage}") 0% 0% / cover no-repeat lightgray`
                  : "lightgray",
              }}
            >
              <>
                {canvas.ImageData ? (
                  <div
                    style={{
                      position: "absolute",
                      top: 10,
                      left: 10,
                      height: 35,
                      width: 120,
                      background: `url("data:image/jpeg;base64,${canvas.ImageData}") 0% 50% / cover no-repeat lightgray`,
                    }}
                  ></div>
                ) : (
                  whenNotImgData
                )}
              </>

              {canvas?.Lines?.map((line) => (
                <Line key={line.Name} line={line} fType={canvas.FormType} />
              ))}
              {canvas?.Rectangles?.map((rectang) => (
                <Rectangle
                  canvas={canvas}
                  key={rectang.Name}
                  fType={canvas.FormType}
                  rectangle={rectang}
                  disabled={isSubmitted}
                />
              ))}
              {canvas.TextBlocks.map((textBlock) => (
                <TextBlock
                  key={textBlock.Name}
                  item={textBlock}
                  fType={canvas.FormType}
                  isBgImage={canvas.BackgroundRefImage ? true : false}
                  disabled={isSubmitted}
                />
              ))}

              {canvas.TextBoxes.map((textBox) => (
                <TextBox
                  key={textBox.Name}
                  item={textBox}
                  isDisabled={isFieldDisabled(textBox.Name)}
                  defaultValue={checkItemInArray(textBox.Name)}
                  isRequired={checkIsRequired(textBox.Name)}
                  changeHandler={props.changeHandler}
                  fType={canvas.FormType}
                  isBgImage={canvas.BackgroundRefImage ? true : false}
                />
              ))}

              {canvas.CheckBoxes.map((checkBox) => (
                <CheckBox
                  key={checkBox.Name}
                  item={checkBox}
                  isDisabled={isFieldDisabled(checkBox.Name)}
                  defaultValue={checkItemInArray(checkBox.Name)}
                  isRequired={checkIsRequired(checkBox.Name)}
                  checkBoxHandler={props.checkBoxHandler}
                  isBgImage={canvas.BackgroundRefImage ? true : false}
                />
              ))}

              {canvas.RadioButtons.map((radio) => (
                <RadioButton
                  key={radio.Name}
                  item={radio}
                  isDisabled={isFieldDisabled(radio.Name)}
                  defaultValue={checkItemInArray(radio.Name)}
                  isRequired={checkIsRequired(radio.Name)}
                  changeHandler={props.changeHandler}
                />
              ))}
            </div>
            <div
              className="viewer-header"
              style={{
                width:
                  canvas?.FormType === "NonAcord"
                    ? `${canvas.Width}px`
                    : `${document.FormViewSize}px`,
                marginTop: "8px",
              }}
            >
              <div
                id="file-name-bottom"
                data-testid="file-name-bottom"
                className="viewer-file-name"
              ></div>
              <div className="viewer-navigation">
                {!isFirstPageOnly && (
                  <IconButton
                    variant="outlined"
                    className="viewer-navigation-button"
                    onClick={onPreviousHandler}
                    disabled={pageIndex === 0}
                  >
                    <NavigateBefore />
                  </IconButton>
                )}
                {showPageNumbers && document?.TotalPages > 1 && (
                  <span className="viewer-page-number">
                    ({pageIndex + 1}/{document.TotalPages})
                  </span>
                )}
                {!isFirstPageOnly && (
                  <IconButton
                    variant="outlined"
                    className="viewer-navigation-button"
                    onClick={onNextHandler}
                    disabled={pageIndex === (document?.TotalPages ?? 1) - 1}
                  >
                    <NavigateNext />
                  </IconButton>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}
XFDLViewer.propTypes = {
  fileName: PropTypes.string,
  isSubmitted: PropTypes.bool,
  from: PropTypes.string,
  showPageNumbers: PropTypes.bool,
  previewType: PropTypes.number,
  document: PropTypes.shape({
    FormViewSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    TotalPages: PropTypes.number,
    Canvas: PropTypes.arrayOf(
      PropTypes.shape({
        Width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        Height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        FormType: PropTypes.string,
        BackgroundRefImage: PropTypes.string,
        ImageData: PropTypes.string,
        IsStaticLogo: PropTypes.bool,
        Lines: PropTypes.array,
        Rectangles: PropTypes.array,
        TextBlocks: PropTypes.array,
        TextBoxes: PropTypes.array,
        CheckBoxes: PropTypes.array,
        RadioButtons: PropTypes.array,
      }),
    ),
  }),
  formData: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.bool,
        PropTypes.object,
        PropTypes.array,
        PropTypes.oneOf([null]),
      ]),
    }),
  ),
  requiredFields: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([
        PropTypes.bool,
        PropTypes.string,
        PropTypes.number,
      ]),
    }),
  ),
  changeHandler: PropTypes.func,
  checkBoxHandler: PropTypes.func,
};

XFDLViewer.defaultProps = {
  fileName: "",
  isSubmitted: false,
  from: "",
  showPageNumbers: false,
  document: undefined,
  formData: [],
  requiredFields: [],
  changeHandler: () => {},
  checkBoxHandler: () => {},
};

export default XFDLViewer;
